import type { ExchangeRates } from '../est/currency/currency.module';
import type { EstGlobalDebounce } from '../estoffice/common/common.module';
import type { UnitStatus } from '../services/api';
import type { RootScope } from '../types';
import type { UnitSearchPanelFilterService } from './unit.search.panel.filter.service';
import type { UnitSearchPanel as UnitSearchPanelType } from './unit.search.panel.resource';

import { FilterSpec, QueryBuilder } from './queryBuilder';

unitSearchPanelCtrl.$inject = [
  'UnitSearchPanel',
  '$scope',
  '$rootScope',
  '$location',
  '$window',
  'unit.search.panel.filter',
  'debounce',
  'exchangeRates',
  'localStorageService',
];

interface ScopeProps {
  houseId: string;
  sectionId: string;

  /**
   * The same as company.currency
   * TODO: migrate to RootScope.company in future?
   */
  defaultCurrency: string | 'false';

  verticalLayout?: 'true' | 'false';
}

interface DropdownStatus extends UnitStatus {
  selected: boolean;
}

export interface unitSearchPanelCtrl extends ScopeProps {
  publicMode: boolean;
  company: RootScope['company'];
  exchangeRates: ExchangeRates;
  request: QueryBuilder;

  /**
   * Status list for filter dropdown
   */
  availableStatuses: DropdownStatus[];
  initStatusList(statuses: UnitStatus[]): void;
  onStatusSelect(data: DropdownStatus): void;

  setNaviMode(mode: RootScope['naviMode']): void;
  toggleStackView(): void;
  togglePriceView(): void;
  toggleShowSold(): void;
  resetQuery(): void;
  updateExchangeRate(selectedCurrency: string): void;
}

export function unitSearchPanelCtrl(
  this: unitSearchPanelCtrl,
  UnitSearchPanel: UnitSearchPanelType,
  $scope: angular.IScope,
  $rootScope: RootScope,
  $location: angular.ILocationService,
  $window: angular.IWindowService,
  filterService: UnitSearchPanelFilterService,
  debounce: EstGlobalDebounce,
  exchangeRates: ExchangeRates,
  localStorageService: angular.local.storage.ILocalStorageService,
) {
  const vm = this;
  vm.publicMode = $location.absUrl().indexOf('/public') > 0;
  vm.company = $rootScope.company;
  vm.exchangeRates = exchangeRates;
  vm.availableStatuses = [];

  $rootScope.$on('unit.search.panel:resetFilters', () => vm.resetQuery());

  $rootScope.updateCurrencyRates();

  vm.request = QueryBuilder.load({
    defaultCurrency: vm.defaultCurrency,
    exchangeRates: vm.exchangeRates,
    localStorageService,
  });

  $scope.$watch(
    'vm.request',
    function () {
      const filters: FilterSpec[] = vm.request.toFilters();

      // we need at least one filter active (except status)
      // in order to start performing filtration
      if (filters.some((item) => item.name !== 'status_id')) {
        debounce(() => {
          filterService.requestFilter({
            sectionId: vm.sectionId,
            publicMode: $location.absUrl().includes('/public'),
            byHouseMode: $location.absUrl().includes('/house'),
            filters,
          });

          vm.request.save();
        }, 400);
      } else {
        filterService.reset();
      }
    },
    true,
  );

  UnitSearchPanel.getStatuses((statuses) => {
    statuses.sort((a, b) => Number(a.type) - Number(b.type));

    vm.initStatusList(statuses);
  });

  vm.onStatusSelect = (data) => vm.request.statuses.toggleSelection(data.id);

  vm.setNaviMode = (mode) => {
    $rootScope.naviMode = mode;
    localStorageService.set('stackingPlanNaviMode', mode);

    if (mode === 'byHouse') {
      $window.location.href = `/house/${vm.houseId}`;
    } else {
      $window.location.href = `/section/${vm.sectionId}`;
    }
  };

  $scope.$watch('$root.company', function () {
    if ($rootScope.company) {
      vm.company = $rootScope.company;
    }
  });

  vm.toggleStackView = () => {
    $rootScope.stackIsMinimized = !$rootScope.stackIsMinimized;

    if ($rootScope.stackIsMinimized) {
      localStorageService.set('stackIsMinimized', true);
    } else {
      localStorageService.remove('stackIsMinimized');
    }
  };

  vm.togglePriceView = () => {
    $rootScope.showPriceInView = !$rootScope.showPriceInView;

    if ($rootScope.showPriceInView) {
      if ($rootScope.stackIsMinimized) {
        vm.toggleStackView();
      }

      localStorageService.set('showPriceInView', true);
    } else {
      localStorageService.remove('showPriceInView');
    }
  };

  vm.toggleShowSold = () => {
    $rootScope.showSold = !$rootScope.showSold;

    if ($rootScope.showSold) {
      localStorageService.remove('hideSold');
    } else {
      localStorageService.set('hideSold', true);
    }
  };

  vm.resetQuery = () => {
    vm.request.reset();

    vm.initStatusList(vm.availableStatuses);
  };

  vm.initStatusList = (statuses) => {
    const hasSavedState = QueryBuilder.hasSavedState(localStorageService);

    vm.availableStatuses = statuses.map((status) => {
      // if we start with empty localStorage, let apply defaults
      if (!hasSavedState) {
        switch (status.type) {
          case '2': // free
          case '3': // reserved
            vm.request.statuses.addSelection(status.id);
            break;
          case '1': // closed
          case '4': // sold
          default:
            break;
        }
      }

      return {
        ...status,
        selected: vm.request.statuses.containsSelection(status.id),
      };
    });
  };

  vm.updateExchangeRate = (selectedCurrency) => {
    vm.request.price.setCurrency(selectedCurrency);
  };
}
